import { useEffect, useState } from "react";
import { Card, Col, Form, Input, Row, Select, Upload } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  getByFlowerId,
  updateFlower,
} from "../../../redux/action/flower";
import { dispatch, useSelector } from "../../../redux/store";
import ButtonBottom from "../../../global/item/ButtonComponent/ButtonBottom";
import { openNotification } from "../../../global/item/Notification/Notification";
import Loading from "../../../global/item/Loading/loading";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getGroup } from "../../../redux/action/groups";

const { Dragger } = Upload;

function ProductUpdateScreen() {
  const { t } = useTranslation();
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [localFlower, setLocalFlower] = useState<any>(null);

  const [form] = Form.useForm();
  const { flowerDetail } = useSelector((state) => state.flower);
  const { groupList } = useSelector((state) => state.group);

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (id) dispatch(getByFlowerId(Number(id)));
    dispatch(getGroup());
  }, [id]);

  /* ================= SYNC LOCAL FLOWER - FIX ĐÂY ================= */
  useEffect(() => {
    if (flowerDetail) {
      // ✅ Luôn sync localFlower với flowerDetail mới nhất từ Redux
      setLocalFlower(flowerDetail);
      form.setFieldsValue({
        name: flowerDetail.name,
        groupId: flowerDetail.groupId,
        lifespan: flowerDetail.lifespan,
        origin: flowerDetail.origin,
        price: flowerDetail.price,
        feature: flowerDetail.feature,
        meaning: flowerDetail.meaning,
        description: flowerDetail.description,
      });
    }
  }, [flowerDetail, form]); // ✅ flowerDetail thay đổi → sync lại localFlower

  /* ================= UPLOAD NEW IMAGE ================= */
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setUploadedImages((prev) => [...prev, e.target!.result as string]);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleRemoveNewImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= DELETE OLD IMAGE ================= */
  const handleRemoveOldImage = async (index: number) => {
    if (!localFlower?.images?.[index] || !id) return;

    const imageId = localFlower.images[index].imageId;

    try {
      setDeleting(true);

      // ✅ Gọi API delete
      await dispatch(
        updateFlower({
          id: Number(id),
          flowerData: {
            deleteImageIds: [Number(imageId)],
          },
        })
      ).unwrap(); // ✅ unwrap() để đợi Redux cập nhật xong

      // ✅ Sau khi API thành công, Redux đã cập nhật flowerDetail
      // → useEffect sẽ tự động sync lại localFlower

      openNotification({
        type: "success",
        message: "Thành công",
        description: "Xóa ảnh thành công!",
      });
    } catch {
      openNotification({
        type: "error",
        message: "Lỗi",
        description: "Xóa ảnh thất bại!",
      });
    } finally {
      setDeleting(false);
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const updatedData: any = {};

      // So sánh và chỉ gửi những field thay đổi
      Object.keys(values).forEach((key) => {
        if (values[key] !== localFlower[key]) {
          updatedData[key] = values[key];
        }
      });

      // ✅ Upload ảnh mới và format đúng theo type addImages
      if (uploadedImages.length > 0) {
        const addImages: { imageUrl: string; imageType: string }[] = [];

        for (const image of uploadedImages) {
          const blob = await fetch(image).then((res) => res.blob());
          const formData = new FormData();
          formData.append("file", blob);
          formData.append("upload_preset", "hoatuoi");

          const res = await fetch(
            "https://api.cloudinary.com/v1_1/dcxwarx50/image/upload",
            { method: "POST", body: formData }
          );
          const data = await res.json();
          
          addImages.push({
            imageUrl: data.secure_url,
            imageType: "Anh1"
          });
        }

        updatedData.addImages = addImages;
      }

      // Kiểm tra có dữ liệu thay đổi không
      if (Object.keys(updatedData).length === 0) {
        openNotification({
          type: "info",
          message: "Thông báo",
          description: "Không có dữ liệu thay đổi!",
        });
        return;
      }

      // ✅ Gọi API update
      await dispatch(
        updateFlower({
          id: Number(id),
          flowerData: updatedData,
        })
      ).unwrap();

      openNotification({
        type: "success",
        message: t("update"),
        description: t("messUpdate"),
      });

      navigate("/product/data");
    } catch (err) {
      openNotification({
        type: "error",
        message: "Lỗi",
        description: "Cập nhật thất bại!",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ================= INITIAL LOADING ================= */
  if (!localFlower || deleting || loading) {
    return <Loading />;
  }

  /* ================= RENDER ================= */
  return (
    <div style={{ position: "relative" }}>
      <Form
        form={form}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        autoComplete="off"
        requiredMark={false}
      >
        {/* ================= BASIC INFO ================= */}
        <Card className="layoutItem">
          <h3 style={{ color: "var(--primary-color)", marginBottom: 8 }}>
            {t("basicInformation")}
          </h3>

          <Row gutter={[20, 0]}>
            <Col xl={12}>
              <Form.Item label="Tên sản phẩm" name="name">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item
                label="Danh mục"
                name="groupId"
                rules={[{ required: true, message: "Vui lòng chọn danh mục" }]}
              >
                <Select showSearch allowClear>
                  {groupList?.map((g) => (
                    <Select.Option key={g.groupId} value={g.groupId}>
                      {g.groupName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Vòng đời" name="lifespan">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Nguồn gốc" name="origin">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Giá" name="price">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Đặc điểm" name="feature">
                <Input.TextArea />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Ý nghĩa" name="meaning">
                <Input.TextArea />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label="Mô tả" name="description">
                <Input.TextArea />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ================= IMAGES ================= */}
        <Card className="layoutItem">
          <h3 style={{ color: "var(--primary-color)", paddingBottom: 10 }}>
            {t("imageInfo")}
          </h3>

          <Row gutter={[16, 16]}>
            {localFlower.images?.map((img: any, idx: number) => (
              <Col xl={6} key={img.imageId}>
                <div style={{ position: "relative" }}>
                  <CloseOutlined
                    onClick={() => handleRemoveOldImage(idx)}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      background: "#fff",
                      borderRadius: "50%",
                      padding: 6,
                      cursor: "pointer",
                      zIndex: 1,
                    }}
                  />
                  <img
                    src={img.imageUrl}
                    alt={`Flower ${idx}`}
                    style={{
                      width: "100%",
                      height: 200,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                </div>
              </Col>
            ))}

            {uploadedImages.map((img, idx) => (
              <Col xl={6} key={`new-${idx}`}>
                <div style={{ position: "relative" }}>
                  <CloseOutlined
                    onClick={() => handleRemoveNewImage(idx)}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      background: "#fff",
                      borderRadius: "50%",
                      padding: 6,
                      cursor: "pointer",
                    }}
                  />
                  <img
                    src={img}
                    alt={`New ${idx}`}
                    style={{
                      width: "100%",
                      height: 200,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                </div>
              </Col>
            ))}
          </Row>

          <Form.Item>
            <Dragger
              multiple
              showUploadList={false}
              beforeUpload={handleFileUpload}
            >
              <PlusOutlined style={{ fontSize: 40 }} />
              <p>{uploadedImages.length ? "Thêm ảnh" : "Tải ảnh lên"}</p>
            </Dragger>
          </Form.Item>
        </Card>

        <ButtonBottom
          onContinueClick={handleSubmit}
          btnCancel={t("cancel")}
          btnContinue={t("continue")}
        />
      </Form>
    </div>
  );
}

export default ProductUpdateScreen;